import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'fw-theme';

/**
 * Sprint L audit cycle 1 (2026-05-13): default to dark per brand.
 *
 * Pre-fix this fell back to `prefers-color-scheme` whenever localStorage
 * was empty, which silently overrode the inline bootstrap script in
 * `client/index.html` (which already defaults to dark per the
 * audit-sweep 2026-05-12 fix). Sunlit-OS-light laptops were rendering
 * the app light on first load despite CLAUDE.md R7 ("dark-mode-first;
 * light is parity, not the default").
 *
 * Fix: read whatever the inline bootstrap already set on
 * `<html data-theme>` and trust it. Bootstrap is the single source of
 * truth — if it ever changes default, this hook follows automatically.
 * On SSR (`window === undefined`) and on malformed/missing attribute,
 * fall back to 'dark' directly. The OS preference is no longer
 * consulted at any point.
 */
export function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  const fromDom = document.documentElement.getAttribute('data-theme');
  if (fromDom === 'dark' || fromDom === 'light') return fromDom;
  return 'dark';
}

/**
 * Brand Guidelines v1.0 surface colors for the browser/OS chrome
 * (`<meta name="theme-color">`): Ink on dark, Mist on light. These are the two
 * canonical background colors, so the address bar / status bar / task switcher
 * matches the app instead of being pinned to one theme.
 */
const THEME_COLOR: Record<Theme, string> = {
  dark: '#06131E', // Ink
  light: '#F1F8FD', // Mist
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem(STORAGE_KEY, theme);

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo(() => ({ theme, setTheme, toggle }), [theme, setTheme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider.');
  return ctx;
}

/**
 * Read-only theme that does NOT require a ThemeProvider.
 *
 * For purely presentational, theme-aware chrome (the brand mark) that may be
 * rendered outside the provider — e.g. a component test that mounts a layout in
 * isolation. Falls back to the `data-theme` attribute the inline bootstrap in
 * `client/index.html` always sets before paint, then to the brand default (dark).
 *
 * Use `useTheme()` when you need to CHANGE the theme; use this to react to it.
 */
export function useThemeValue(): Theme {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx.theme;
  return readInitialTheme();
}
